/**
 * AdminOrdersBoard
 * ----------------
 * Command Center for hospital ORDERS (from /marketplace cart submissions).
 * Mirrors the Backorders board UX so admins have a consistent muscle memory.
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ KPI strip (clickable filters)                                    │
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │ Status tabs · Search · Refresh                                   │
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │ Order list (table)                                               │
 *   │   Row click → slide-out drawer with                              │
 *   │      • Details/edit • Fulfillment (tracking/PO/dates)            │
 *   │      • Communications log • Progress notes timeline              │
 *   │      • Email Hospital (mailto drafts)                            │
 *   └──────────────────────────────────────────────────────────────────┘
 */

import { Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { canViewCost } from "@/lib/costAccess";
import {
  Search, RefreshCw, Mail, X, Loader2, Package, Truck, MapPin, ListChecks,
  AlertCircle, CheckCircle2, Pause, Trash2, FileText, MessageSquare, Plus,
  DollarSign, ClipboardList, Send, Edit3, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import BackorderFulfillmentTab from "@/components/admin/BackorderFulfillmentTab";
import AddProductDialog from "@/components/admin/AddProductDialog";
import ProposalsPanel from "@/components/admin/ProposalsPanel";
import { JANITORIAL_PRODUCTS } from "@/data/janitorialCatalog";
import { ASC_PRODUCTS } from "@/data/americanSurgicalCatalog";

// Parse a price hint string like "$0.29 / wipe" or "$116.00 / case of 8 boxes"
// into { unit: number, uom: string }. Returns null when no $ figure is found.
function parsePriceHint(str) {
  if (!str || typeof str !== "string") return null;
  const m = str.match(/\$([\d,]+\.?\d*)/);
  if (!m) return null;
  const price = parseFloat(m[1].replace(/,/g, ""));
  if (isNaN(price)) return null;
  let uom = "Each";
  const lower = str.toLowerCase();
  if (lower.includes("case")) uom = "Case";
  else if (lower.includes("box")) uom = "Box";
  else if (lower.includes("pack")) uom = "Pack";
  else if (lower.includes("pair")) uom = "Pair";
  else if (lower.includes("roll")) uom = "Roll";
  return { unit: price, uom, label: str };
}

// Build a flat searchable list of marketplace SKUs the picker can offer.
// Pulls from janitorial catalog + American Surgical catalog (flattens SKU groups).
// Each row: { sku, name, category, image?, unit_cost?, uom?, price_label? }
function buildMarketplaceIndex() {
  const out = [];
  // Janitorial — use the per-unit price as the default cost where available,
  // otherwise fall back to per-case. Admin can still edit the line.
  for (const p of JANITORIAL_PRODUCTS || []) {
    const unit = parsePriceHint(p?.pricing?.unit);
    const fallback = parsePriceHint(p?.pricing?.case || p?.pricing?.box);
    const pick = unit || fallback;
    out.push({
      sku: p.sku,
      name: p.name,
      category: p.category,
      image: p.image,
      unit_cost: pick?.unit ?? null,
      uom: pick?.uom ?? "Each",
      price_label: p?.pricing?.unit || p?.pricing?.case || p?.pricing?.box || null,
      source: "Janitorial · Sterile Mate",
    });
  }
  // American Surgical — flatten SKU groups (Ray-Cot, Delicot, Americot, etc.)
  for (const fam of ASC_PRODUCTS || []) {
    for (const grp of fam.skuGroups || []) {
      for (const row of grp.rows || []) {
        out.push({
          sku: row.code,
          name: `${fam.name} ${grp.label || ""} ${row.a || ""}`.trim().replace(/\s+/g, " "),
          category: "american-surgical",
          image: fam.img,
          unit_cost: null,
          uom: "Case",
          price_label: fam.packaging || null,
          source: "American Surgical",
        });
      }
    }
  }
  return out;
}

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  new:                       { label: "New",             cls: "bg-blue-50 text-blue-700 border-blue-200" },
  pending_account_approval:  { label: "Pending Approval", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  quoted:                    { label: "Quoted",          cls: "bg-amber-50 text-amber-700 border-amber-200" },
  po_received:               { label: "PO Received",     cls: "bg-violet-50 text-violet-700 border-violet-200" },
  in_fulfillment:            { label: "In Fulfillment",  cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  shipped:                   { label: "Shipped",         cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  delivered:                 { label: "Delivered",       cls: "bg-slate-100 text-slate-700 border-slate-300" },
  on_hold:                   { label: "On Hold",         cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  cancelled:                 { label: "Cancelled",       cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const SOURCE_META = {
  cart:        { label: "Marketplace", cls: "bg-emerald-50 text-emerald-700" },
  quote_form:  { label: "Quote Form",  cls: "bg-blue-50 text-blue-700" },
  manual:      { label: "Manual",      cls: "bg-amber-50 text-amber-700" },
};

const fmt$ = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Supplier cost and gross profit for one order, from the line items.
const orderGp = (o) => {
  let revenue = 0, cost = 0, missing = 0;
  for (const it of o?.items || []) {
    const qty = Number(it.quantity) || 0;
    const price = Number(it.unit_price ?? it.unit_cost) || 0;
    revenue += qty * price;
    if (it.internal_cost == null || it.internal_cost === "") missing += 1;
    else cost += qty * Number(it.internal_cost);
  }
  const gp = revenue - cost;
  return {
    revenue: Math.round(revenue * 100) / 100,
    cost: Math.round(cost * 100) / 100,
    gp: Math.round(gp * 100) / 100,
    gpPct: revenue > 0 ? Math.round((gp / revenue) * 1000) / 10 : null,
    missing,
  };
};

const fmtAge = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};
const fmtTs = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status || "—", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${m.cls}`}>{m.label}</span>;
}
function SourceBadge({ source }) {
  const m = SOURCE_META[source] || { label: source || "—", cls: "bg-slate-100 text-slate-500" };
  return <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${m.cls}`}>{m.label}</span>;
}

// ============================================================================
export default function AdminOrdersBoard() {
  const { token, user } = useAuth();
  const seeCost = canViewCost(user);
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/orders`, {
        headers, params: { status: statusFilter, q: search || undefined, limit: 200 },
      });
      setItems(r.data.items || []);
    } catch {
      toast.error("Failed to load orders");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter]);

  // Server-side search — debounced so we hit /api/admin/orders whenever the
  // user types. Without this the search bar was capped at the client-side
  // window of 200 recently-loaded orders (why long-tail IDs like CO-241460E3
  // felt "missing"). Empty string triggers a plain reload.
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [search]);

  const deleteOrder = async (id) => {
    if (!window.confirm(`Delete order ${id}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/orders/${id}`, { headers });
      toast.success(`Deleted ${id}`);
      if (selectedId === id) setSelectedId(null);
      setItems((p) => p.filter((it) => it.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const stats = useMemo(() => {
    const base = { all: items.length, new: 0, quoted: 0, po_received: 0, in_fulfillment: 0, shipped: 0, delivered: 0, on_hold: 0 };
    items.forEach((it) => { const s = it.status || "new"; if (base[s] !== undefined) base[s] += 1; });
    return base;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (!q) return true;
      const hay = `${it.id || ""} ${it.hospital_name || ""} ${it.item_name || ""} ${it.contact_name || ""} ${it.contact_email || ""} ${it.po_number || ""} ${it.tracking_number || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  const selected = useMemo(() => items.find((i) => i.id === selectedId) || null, [items, selectedId]);

  return (
    <div className="space-y-5" data-testid="admin-orders-board">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Orders Command Center</h1>
          <p className="text-sm text-slate-500">
            Quote-cart submissions and direct hospital orders. Track status, communications, tracking #, and progress notes &mdash; all in one place.
          </p>
        </div>
        <Button
          onClick={() => setShowNew(true)}
          className="bg-amber-600 hover:bg-amber-700 gap-1.5"
          data-testid="orders-new-customer-order-btn"
        >
          <Plus className="w-4 h-4" /> New Customer Order
        </Button>
      </div>

      <ProposalsPanel token={token} />

      {showNew && (
        <NewCustomerOrderInline
          open={showNew}
          onClose={() => setShowNew(false)}
          onSuccess={load}
          token={token}
        />
      )}

      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {[
          { key: "all",            label: "All",          icon: Package,      color: "bg-slate-100 text-slate-700" },
          { key: "new",            label: "New",          icon: AlertCircle,  color: "bg-blue-50 text-blue-700" },
          { key: "quoted",         label: "Quoted",       icon: ClipboardList,color: "bg-amber-50 text-amber-700" },
          { key: "po_received",    label: "PO In",        icon: DollarSign,   color: "bg-violet-50 text-violet-700" },
          { key: "in_fulfillment", label: "Fulfilling",   icon: ListChecks,   color: "bg-cyan-50 text-cyan-700" },
          { key: "shipped",        label: "Shipped",      icon: Truck,        color: "bg-emerald-50 text-emerald-700" },
          { key: "delivered",      label: "Delivered",    icon: CheckCircle2, color: "bg-slate-50 text-slate-700" },
          { key: "on_hold",        label: "On Hold",      icon: Pause,        color: "bg-yellow-50 text-yellow-700" },
        ].map((k) => {
          const active = statusFilter === k.key;
          return (
            <button
              key={k.key}
              onClick={() => setStatusFilter(k.key)}
              className={`text-left rounded-xl border p-3 transition-all bg-white ${active ? "border-amber-400 ring-2 ring-amber-100" : "border-slate-200 hover:border-slate-300 hover:shadow-sm"}`}
              data-testid={`ord-kpi-${k.key}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-7 h-7 rounded-md flex items-center justify-center ${k.color}`}>
                  <k.icon className="w-3.5 h-3.5" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{k.label}</span>
              </div>
              <div className="text-2xl font-extrabold text-slate-900 leading-none mt-1">{stats[k.key] ?? 0}</div>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by ID, hospital, contact, PO, tracking…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="ord-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </Button>
      </div>

      {/* Order Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="px-4 py-2 text-left">Order</th>
                <th className="px-4 py-2 text-left">Hospital</th>
                <th className="px-4 py-2 text-left">Items</th>
                <th className="px-4 py-2 text-right">Total</th>
                {seeCost && (
                  <th className="px-4 py-2 text-right text-amber-700" title="Gross profit — admin-only, never shown to the customer">🔒 GP</th>
                )}
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Tracking</th>
                <th className="px-4 py-2 text-left">Age</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={seeCost ? 9 : 8} className="px-4 py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={seeCost ? 9 : 8} className="px-4 py-10 text-center text-slate-500">No orders match.</td></tr>
              )}
              {!loading && filtered.map((it) => {
                const lineCount = (it.items || []).length;
                const first = (it.items?.[0]?.name) || it.item_name || "—";
                return (
                  <tr key={it.id} onClick={() => setSelectedId(it.id)} className="hover:bg-amber-50/40 cursor-pointer" data-testid={`ord-row-${it.id}`}>
                    <td className="px-4 py-2.5 align-top">
                      <div className="flex flex-col gap-1">
                        <span className="font-mono text-xs font-bold text-slate-900">{it.id}</span>
                        <SourceBadge source={it.source} />
                      </div>
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <div className="text-sm font-semibold text-slate-900">{it.hospital_name || "—"}</div>
                      <div className="text-[11px] text-slate-500">{it.contact_name || ""}</div>
                      <div className="text-[11px] text-slate-500">{it.contact_email || ""}</div>
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <div className="text-xs text-slate-700 truncate max-w-[220px]">{first}</div>
                      {lineCount > 1 && <div className="text-[10px] text-slate-400">+{lineCount - 1} more</div>}
                    </td>
                    <td className="px-4 py-2.5 align-top text-right">
                      <div className="font-bold text-emerald-700 tabular-nums">{fmt$(it.total ?? it.transaction_total)}</div>
                      {it.use_hospital_courier ? (
                        <div className="text-[10px] text-slate-400">own freight</div>
                      ) : ((it.subtotal ?? it.items_subtotal ?? 0) > 0 && (
                        <div className="text-[10px] text-slate-400">incl. {fmt$(it.freight_cost)} freight</div>
                      ))}
                    </td>
                    {seeCost && (() => {
                      const g = orderGp(it);
                      return (
                        <td className="px-4 py-2.5 align-top text-right" data-testid={`ord-gp-${it.id}`}>
                          {g.revenue > 0 ? (
                            <>
                              <div className={`font-bold tabular-nums ${g.gpPct == null ? "text-slate-400" : g.gpPct >= 25 ? "text-emerald-700" : g.gpPct >= 12 ? "text-amber-700" : "text-rose-700"}`}>
                                {g.gpPct == null ? "—" : `${g.gpPct}%`}
                              </div>
                              <div className="text-[10px] text-slate-400">{fmt$(g.gp)}</div>
                              {g.missing > 0 && (
                                <div className="text-[10px] font-bold text-rose-600" title="Supplier cost missing on some lines — margin overstated">
                                  no cost ×{g.missing}
                                </div>
                              )}
                            </>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                      );
                    })()}
                    <td className="px-4 py-2.5 align-top"><StatusPill status={it.status} /></td>
                    <td className="px-4 py-2.5 align-top text-xs text-slate-700">
                      {it.tracking_number ? (
                        <>
                          <div className="font-mono">{it.tracking_number}</div>
                          <div className="text-[10px] text-slate-400">{it.carrier || ""}</div>
                        </>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5 align-top text-xs text-slate-500">{fmtAge(it.created_at)}</td>
                    <td className="px-4 py-2.5 align-top text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteOrder(it.id); }}
                        className="text-rose-400 hover:text-rose-600 p-1"
                        data-testid={`ord-delete-${it.id}`}
                        aria-label="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <OrderDetailDrawer
          order={selected}
          onClose={() => setSelectedId(null)}
          onDelete={() => deleteOrder(selected.id)}
          onChanged={load}
          token={token}
        />
      )}
    </div>
  );
}

// ============================================================================
function OrderDetailDrawer({ order, onClose, onDelete, onChanged, token }) {
  const { user: me } = useAuth();
  const seeCost = canViewCost(me);
  const headers = { Authorization: `Bearer ${token}` };
  const [tab, setTab] = useState("details");
  const [draft, setDraft] = useState(order);
  const [editing, setEditing] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [newComm, setNewComm] = useState({ channel: "email", direction: "out", summary: "", contact: order.contact_name || "" });
  const [emailMenuOpen, setEmailMenuOpen] = useState(false);
  const [notifyMenuOpen, setNotifyMenuOpen] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [shareLinkMenuOpen, setShareLinkMenuOpen] = useState(false);

  useEffect(() => { setDraft(order); }, [order]);

  const patch = async (changes) => {
    try {
      const r = await axios.patch(`${API}/api/admin/orders/${order.id}`, changes, { headers });
      setDraft(r.data.order);
      onChanged?.();
      return r.data.order;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const changeStatus = async (newStatus) => {
    const updated = await patch({ status: newStatus });
    if (updated) toast.success(`Status → ${STATUS_META[newStatus]?.label || newStatus}`);
  };

  // Item edit helpers (drawer) — mutations run against the draft; saveEdits
  // then PATCHes the whole items array so the backend can recompute totals.
  const setItemField = (idx, field, val) => {
    setDraft((d) => ({
      ...d,
      items: (d.items || []).map((it, i) => {
        if (i !== idx) return it;
        const next = { ...it, [field]: val };
        // Live per-line recompute so subtotal / total feels responsive.
        const q = parseFloat(field === "quantity" ? val : next.quantity) || 0;
        const p = parseFloat(field === "unit_price" ? val : (next.unit_price ?? next.unit_cost)) || 0;
        next.line_total = +(q * p).toFixed(2);
        if (field === "unit_price") next.unit_cost = next.unit_price;
        return next;
      }),
    }));
  };

  const addOrderItem = () => {
    setDraft((d) => ({
      ...d,
      items: [
        ...(d.items || []),
        { name: "", sku: "", quantity: 1, unit_price: 0, unit_cost: 0, internal_cost: null, line_total: 0, description: "" },
      ],
    }));
    setEditing(true);
  };

  const removeOrderItem = (idx) => {
    const it = (draft.items || [])[idx] || {};
    const label = it.name || it.sku || `line ${idx + 1}`;
    if (!window.confirm(`Remove "${label}" from this order? Save changes to make it permanent.`)) return;
    setDraft((d) => ({ ...d, items: (d.items || []).filter((_, i) => i !== idx) }));
  };

  // Live client-side subtotal (backend also re-derives on save)
  const liveSubtotal = (draft.items || []).reduce((s, it) => {
    const q = parseFloat(it.quantity) || 0;
    const p = parseFloat(it.unit_price ?? it.unit_cost) || 0;
    return s + q * p;
  }, 0);

  const saveEdits = async () => {
    const updated = await patch({
      tracking_number: draft.tracking_number || "",
      carrier: draft.carrier || "",
      po_number: draft.po_number || "",
      payment_terms: draft.payment_terms || "Net 30",
      expected_delivery: draft.expected_delivery || "",
      shipping_address: draft.shipping_address || "",
      admin_notes: draft.admin_notes || "",
      use_hospital_courier: !!draft.use_hospital_courier,
      hospital_courier_account: draft.hospital_courier_account || "",
      freight_cost: draft.freight_cost === "" || draft.freight_cost == null
        ? null
        : parseFloat(draft.freight_cost),
      items: (draft.items || []).map((it) => ({
        ...it,
        quantity: parseFloat(it.quantity) || 0,
        unit_price: it.unit_price === "" || it.unit_price == null ? 0 : parseFloat(it.unit_price),
        unit_cost: it.unit_cost === "" || it.unit_cost == null ? undefined : parseFloat(it.unit_cost),
        internal_cost: it.internal_cost === "" || it.internal_cost == null ? null : parseFloat(it.internal_cost),
      })),
    });
    if (updated) { toast.success("Saved"); setEditing(false); }
  };

  // One-click approval of the auto-drafted hospital account that came in with
  // this order. Calls the admin endpoint, which sends the welcome email and
  // releases all pending_account_approval orders for the same email. When the
  // auto-send fails (Resend sandbox / Graph not configured), the admin can
  // click "Send via Outlook" to open their default mail client pre-filled.
  const [manualWelcomeEmail, setManualWelcomeEmail] = useState(null);
  const openManualEmailInOutlook = (payload) => {
    const params = new URLSearchParams({
      subject: payload.subject || "Welcome to Sterile Mate",
      body: payload.plain_body || "",
    }).toString().replace(/\+/g, "%20");
    window.location.href = `mailto:${encodeURIComponent(payload.recipient)}?${params}`;
  };

  const approveHospitalAccount = async ({ silent = false } = {}) => {
    const requestId = draft.pending_hospital_request_id;
    if (!requestId) {
      toast.error("No pending hospital registration linked to this order");
      return;
    }
    try {
      const url = silent
        ? `${API}/api/hospitals/register/approve/${requestId}?skip_email=true`
        : `${API}/api/hospitals/register/approve/${requestId}`;
      const r = await axios.post(url, {}, { headers });
      const email = r.data?.email || {};
      if (silent || email.status === "skipped") {
        toast.success(`Account approved silently · no welcome email sent`, {
          description: "Share the access code with the hospital directly.",
        });
        setManualWelcomeEmail(null);
      } else if (email.status === "sent") {
        toast.success(`Account approved · welcome email delivered to ${draft.contact_email}`);
        setManualWelcomeEmail(null);
      } else {
        toast.warning("Approved — auto-send failed. Use the Send-via-Outlook button below.");
        setManualWelcomeEmail({ ...email, hospital_name: draft.hospital_name });
      }
      // Patch local state — the order's status was bumped server-side.
      setDraft({
        ...draft,
        is_new_customer: false,
        pending_hospital_request_id: null,
        status: (draft.po_number || "").trim() ? "po_received" : "new",
      });
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const r = await axios.post(`${API}/api/admin/orders/${order.id}/note`, { text: newNote.trim() }, { headers });
      setDraft((d) => ({ ...d, progress_notes: [...(d.progress_notes || []), r.data.note] }));
      setNewNote("");
      onChanged?.();
    } catch { toast.error("Failed to add note"); }
  };

  const addComm = async () => {
    if (!newComm.summary.trim()) return;
    try {
      const r = await axios.post(`${API}/api/admin/orders/${order.id}/communication`, newComm, { headers });
      setDraft((d) => ({ ...d, communications: [...(d.communications || []), r.data.communication] }));
      setNewComm({ ...newComm, summary: "" });
      onChanged?.();
      toast.success("Communication logged");
    } catch { toast.error("Failed to log communication"); }
  };

  const emailHospital = async (template) => {
    setEmailMenuOpen(false);
    try {
      const r = await axios.get(`${API}/api/admin/orders/${order.id}/email-hospital-draft?template=${template}`, { headers });
      window.location.href = r.data.mailto;
      const ccList = (r.data.cc || []).join(", ");
      const repNote = r.data.rep_email ? ` (CC: ${r.data.rep_email})` : "";
      toast.success(`Outlook draft opened — to ${r.data.to}${repNote}`);
      // Auto-log the communication (include CC info so the timeline reflects who was looped in)
      await axios.post(`${API}/api/admin/orders/${order.id}/communication`, {
        channel: "email", direction: "out",
        summary: `Sent template "${template.replace("_", " ")}"${ccList ? ` · CC: ${ccList}` : ""}`,
        contact: r.data.to,
      }, { headers });
      onChanged?.();
    } catch { toast.error("Could not build email draft"); }
  };

  // ── Customer Order Portal — shareable estimate approval link ────────────
  // Generates (or rotates) a token-protected /order/{token} URL the customer
  // can use to review the estimate, adjust qty, attach a PO#, upload PO PDF,
  // and approve as guest or account. On approval the order flips to
  // po_received and shows up in the fulfillment queue.
  const generateAndCopyLink = async (rotate = false) => {
    setShareLinkMenuOpen(false);
    setSendingLink(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/orders/${order.id}/share-link`,
        { rotate },
        { headers },
      );
      try {
        await navigator.clipboard.writeText(r.data.share_url);
        toast.success(rotate ? "New link generated & copied to clipboard" : "Order link copied to clipboard");
      } catch {
        toast.success(`Link: ${r.data.share_url}`);
      }
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate share link");
    } finally {
      setSendingLink(false);
    }
  };

  const emailShareLink = async () => {
    setShareLinkMenuOpen(false);
    if (!order.contact_email) {
      toast.error("No customer email on file — set contact email first");
      return;
    }
    setSendingLink(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/orders/${order.id}/send-share-link`,
        { to_email: order.contact_email },
        { headers },
      );
      toast.success(`Order link emailed to ${r.data.to}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not email share link");
    } finally {
      setSendingLink(false);
    }
  };

  // Themed multi-template notification engine — sends an HTML email rendered
  // from the order document (line items, customer PO, freight, tracking, totals).
  // Same 7 templates available on the historical-record backorder side.
  // Open the admin's local mail client (Outlook on desktop) pre-populated
  // with the chosen notification subject, plaintext body, and recipient. The
  // From-address is whatever Outlook is signed in as (admin's
  // george.broughton@sterilemate.com) — mailto: can't override that. The
  // admin can then attach the downloaded PDF + add a personal letter before
  // sending. Great for high-touch quotes where the auto-send template is
  // too generic.
  const composeInOutlook = async (template = "quote_sent") => {
    setNotifyMenuOpen(false);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/orders/${order.id}/notification-preview?template=${encodeURIComponent(template)}`,
        { headers },
      );
      const to = (data.to_email || "").trim();
      if (!to) {
        toast.error("This order has no contact email on file");
        return;
      }
      const params = new URLSearchParams({
        subject: data.subject || "Sterile Mate",
        body: data.plain_body || "",
        cc: data.cc_email || "",
      }).toString().replace(/\+/g, "%20");
      window.location.href = `mailto:${encodeURIComponent(to)}?${params}`;
      toast.success("Outlook opened — attach the PDF and add your letter before sending");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not prepare email");
    }
  };

  const sendNotification = async (template) => {
    setNotifyMenuOpen(false);
    const labels = {
      quote_sent: "Quote", order_confirmation: "Order Confirmation",
      shipping_notification: "Shipping Notification", partial_shipment: "Partial Shipment",
      delivery_confirmation: "Delivery Confirmation", delay_notification: "Delay Notification",
      transaction_record: "Transaction Record",
    };
    const label = labels[template] || template;
    const to = window.prompt(`Send "${label}" to (email):`, order.contact_email || "");
    if (!to || !to.includes("@")) {
      if (to !== null) toast.error("A valid email is required");
      return;
    }
    const ccRaw = window.prompt("CC additional recipients (comma-separated, optional):", "");
    const cc_emails = (ccRaw || "").split(",").map((s) => s.trim()).filter((s) => s.includes("@"));
    try {
      const r = await axios.post(`${API}/api/admin/orders/${order.id}/email-record`,
        { to_email: to.trim(), cc_emails, template }, { headers });
      toast.success(`${label} sent to ${r.data.to}${r.data.cc?.length ? ` (cc ${r.data.cc.length})` : ""}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not send ${label}`);
    }
  };

  // Download any of the 7 notification templates as a printable PDF.
  const downloadPdf = async (template = "transaction_record") => {
    setNotifyMenuOpen(false);
    try {
      const r = await axios.get(
        `${API}/api/admin/orders/${order.id}/transaction-record/pdf?template=${template}`,
        { headers, responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const po = order.customer_po_number || order.po_number || "no-po";
      a.download = `Sterile_Mate_${template}_${order.id}_PO_${po}.pdf`.replace(/\s+/g, "_");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("Could not generate PDF"); }
  };

  const togglePaid = async () => {
    try {
      await axios.patch(`${API}/api/admin/orders/${order.id}/paid`,
        { is_paid: !order.is_paid }, { headers });
      toast.success(order.is_paid ? "Marked unpaid" : "Marked paid");
      onChanged?.();
    } catch { toast.error("Could not update payment status"); }
  };

  const totalLines = (draft.items || []).length;

  return (
    <div className="fixed inset-0 z-40 bg-black/40" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="ord-drawer">
      <div
        className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs font-bold text-slate-900">{draft.id}</span>
              <SourceBadge source={draft.source} />
              <StatusPill status={draft.status} />
            </div>
            <h2 className="text-lg font-bold text-slate-900">{draft.hospital_name || "—"}</h2>
            <p className="text-xs text-slate-500">{fmtAge(draft.created_at)} · {totalLines} line{totalLines === 1 ? "" : "s"} · <span className="font-bold text-emerald-700">{fmt$(draft.total ?? draft.transaction_total)}</span></p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        {/* Action toolbar */}
        <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2 bg-white">
          <div className="relative">
            <Button size="sm" variant="outline" onClick={() => setEmailMenuOpen((v) => !v)} className="gap-1.5" data-testid="ord-drawer-email">
              <Mail className="w-3.5 h-3.5" /> Email Hospital ▾
            </Button>
            {emailMenuOpen && (
              <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-md shadow-lg w-56 py-1" onMouseLeave={() => setEmailMenuOpen(false)}>
                <button onClick={() => emailHospital("status_update")} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50">📊 Status Update</button>
                <button onClick={() => emailHospital("shipped")} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50">🚚 Shipped + Tracking</button>
                <button onClick={() => emailHospital("delay")} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50">⏱ Delay Notice</button>
                <button onClick={() => emailHospital("delivered")} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50">✅ Delivery Confirmation</button>
              </div>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => editing ? saveEdits() : setEditing(true)} className="gap-1.5" data-testid="ord-drawer-edit">
            <Edit3 className="w-3.5 h-3.5" /> {editing ? "Save" : "Edit"}
          </Button>

          {/* Prominent quick action — Download the customer-facing Quote PDF */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => downloadPdf("quote_sent")}
            className="gap-1.5 text-blue-700 border-blue-300 hover:text-blue-800 hover:bg-blue-50 hover:border-blue-400 font-semibold"
            data-testid="ord-drawer-download-quote"
            title="Download the customer-facing Quote PDF (no email sent — just save / print)"
          >
            <Download className="w-3.5 h-3.5" /> Download Quote
          </Button>

          {/* Prominent quick action — Open Outlook with the Quote pre-filled */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => composeInOutlook("quote_sent")}
            className="gap-1.5 text-indigo-700 border-indigo-300 hover:text-indigo-800 hover:bg-indigo-50 hover:border-indigo-400 font-semibold"
            data-testid="ord-drawer-outlook-quote"
            title="Open Outlook with the Quote subject + body pre-filled. From-address will be whatever Outlook is signed in as (e.g. george.broughton@sterilemate.com). Attach the downloaded PDF + add a personal letter before sending."
          >
            <Mail className="w-3.5 h-3.5" /> Email Quote (Outlook)
          </Button>

          {/* Send Order Link — customer approval portal */}
          <div className="relative">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShareLinkMenuOpen((v) => !v)}
              disabled={sendingLink}
              className="gap-1.5 text-amber-700 border-amber-300 hover:text-amber-800 hover:bg-amber-50 hover:border-amber-400 font-semibold"
              data-testid="ord-drawer-share-link"
              title="Generate a shareable customer approval link. The customer can review this estimate, adjust quantities, attach their PO # + PDF, and approve as guest or account."
            >
              <Send className="w-3.5 h-3.5" /> {sendingLink ? "Working…" : "Send Order Link ▾"}
            </Button>
            {shareLinkMenuOpen && (
              <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-md shadow-lg w-64 py-1" onMouseLeave={() => setShareLinkMenuOpen(false)}>
                <button
                  type="button"
                  onClick={() => generateAndCopyLink(false)}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50"
                  data-testid="ord-drawer-share-copy"
                >
                  🔗 Copy link to clipboard
                </button>
                <button
                  type="button"
                  onClick={emailShareLink}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50"
                  data-testid="ord-drawer-share-email"
                >
                  ✉️ Email link to customer
                </button>
                <div className="border-t border-slate-100 my-1" />
                <button
                  type="button"
                  onClick={() => generateAndCopyLink(true)}
                  className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
                  data-testid="ord-drawer-share-rotate"
                >
                  🔄 Rotate (invalidate old link)
                </button>
              </div>
            )}
          </div>

          {/* Send Notification dropdown — 7 themed templates + PDF download */}
          <div className="relative">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setNotifyMenuOpen((v) => !v)}
              className="gap-1.5 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 hover:border-emerald-300"
              title="Send customer notification (quote, order confirmation, shipping, delivery, delay, partial, receipt)"
              data-testid="ord-drawer-notify"
            >
              <Send className="w-3.5 h-3.5" /> Send Notification ▾
            </Button>
            {notifyMenuOpen && (
              <div
                className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-md shadow-lg w-[400px] py-1"
                onMouseLeave={() => setNotifyMenuOpen(false)}
                data-testid="ord-notify-menu"
              >
                <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex justify-between">
                  <span>Template</span>
                  <span>Auto-send · Outlook · PDF</span>
                </div>
                {[
                  { t: "quote_sent", icon: "💬", name: "Send Quote", desc: "Itemized quote — valid 30 days", tid: "quote" },
                  { t: "order_confirmation", icon: "✅", name: "Order Confirmation", desc: "PO acknowledged, processing", tid: "confirm" },
                  { t: "shipping_notification", icon: "🚚", name: "Shipping Notification", desc: "It shipped — tracking included", tid: "shipping" },
                  { t: "partial_shipment", icon: "📦", name: "Partial Shipment", desc: "Some lines shipped, balance to follow", tid: "partial" },
                  { t: "delivery_confirmation", icon: "📬", name: "Delivery Confirmation", desc: "Delivered — please retain for records", tid: "delivered" },
                  { t: "delay_notification", icon: "⏱️", name: "Delay Notification", desc: "Heads up — shipment is delayed", tid: "delay" },
                  { t: "transaction_record", icon: "🧾", name: "Final Transaction Record", desc: "AP-ready receipt with totals", tid: "receipt", divideAbove: true },
                ].map((row) => (
                  <div key={row.t}>
                    {row.divideAbove && <div className="border-t border-slate-200 my-1"></div>}
                    <div className="flex items-stretch group hover:bg-slate-50">
                      <button
                        onClick={() => sendNotification(row.t)}
                        className="flex-1 text-left px-3 py-2 text-sm flex items-start gap-2 min-w-0"
                        data-testid={`ord-notify-${row.tid}`}
                        title={`Auto-send this ${row.name.toLowerCase()} to the customer from the portal`}
                      >
                        <span className="text-base leading-none mt-0.5 shrink-0">{row.icon}</span>
                        <span className="min-w-0">
                          <span className="block font-semibold text-slate-800 truncate">{row.name}</span>
                          <span className="block text-[11px] text-slate-500 truncate">{row.desc}</span>
                        </span>
                      </button>
                      <button
                        onClick={() => composeInOutlook(row.t)}
                        className="px-2.5 py-2 border-l border-slate-100 text-slate-400 hover:text-blue-700 hover:bg-blue-50 flex items-center justify-center shrink-0"
                        data-testid={`ord-notify-${row.tid}-outlook`}
                        title={`Open Outlook with ${row.name} pre-filled — you can attach the PDF and add a personal letter before hitting Send`}
                      >
                        <Mail className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => downloadPdf(row.t)}
                        className="px-2.5 py-2 border-l border-slate-100 text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 flex items-center justify-center shrink-0"
                        data-testid={`ord-notify-${row.tid}-pdf`}
                        title={`Download ${row.name} as PDF — no email sent`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Paid / Unpaid toggle — AP marks Net-30 invoices paid until QBO sync.
              Tooltip surfaces payment terms; click flips state and stamps paid_at/by. */}
          <button
            type="button"
            onClick={togglePaid}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-bold transition-colors ${
              order.is_paid
                ? "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                : "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
            }`}
            title={
              order.is_paid
                ? `Paid${order.paid_at ? ` on ${new Date(order.paid_at).toLocaleDateString()}` : ""}. Click to mark unpaid.`
                : `Unpaid · Terms: ${order.payment_terms || "Net 30"}. Click to mark paid.`
            }
            data-testid="ord-paid-toggle"
            data-state={order.is_paid ? "paid" : "unpaid"}
          >
            <span className={`w-2 h-2 rounded-full ${order.is_paid ? "bg-emerald-500 animate-pulse" : "bg-amber-500"}`} />
            {order.is_paid ? "Paid" : `Unpaid · ${order.payment_terms || "Net 30"}`}
          </button>

          <Button size="sm" variant="outline" onClick={onDelete} className="gap-1.5 text-rose-600 hover:bg-rose-50 hover:border-rose-300" data-testid="ord-drawer-delete">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </Button>
          <select
            value={draft.status || "new"}
            onChange={(e) => changeStatus(e.target.value)}
            className="ml-auto text-xs border border-slate-300 rounded-md px-2 py-1.5"
            data-testid="ord-drawer-status-select"
          >
            {Object.entries(STATUS_META).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </select>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-3 border-b border-slate-200 flex gap-3">
          {[
            { id: "details",  label: "Details",         icon: FileText },
            { id: "ship",     label: "Order Fields",    icon: Package },
            { id: "fulfillment", label: "Fulfillment",  icon: Truck },
            { id: "comms",    label: `Comms (${(draft.communications || []).length})`, icon: MessageSquare },
            { id: "notes",    label: `Notes (${(draft.progress_notes || []).length})`, icon: ClipboardList },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`pb-2 border-b-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${tab === t.id ? "border-amber-600 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            >
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* New-customer banner — shown across all tabs so admins immediately
              see the credit-approval CTA the moment they open the order. */}
          {draft.is_new_customer && (
            <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 px-4 py-3 mb-4" data-testid="ord-new-customer-banner-top">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <p className="text-sm font-bold text-amber-900">NEW CUSTOMER · account approval required</p>
                  <p className="text-xs text-amber-800 mt-0.5">
                    No matching hospital was found in the CRM. Net 30 credit terms take up to 24 hours to approve.
                    Prepay orders can ship immediately on payment receipt.
                  </p>
                </div>
                {draft.pending_hospital_request_id && (
                  <div className="shrink-0 flex gap-1.5">
                    <button
                      onClick={() => approveHospitalAccount({ silent: false })}
                      className="px-3 py-1.5 text-xs font-bold rounded-md bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1.5 shadow-sm"
                      data-testid="ord-approve-hospital"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Approve &amp; Send Welcome
                    </button>
                    <button
                      onClick={() => approveHospitalAccount({ silent: true })}
                      className="px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-700 hover:bg-slate-800 text-white inline-flex items-center gap-1.5 shadow-sm"
                      title="Approve without sending the welcome letter — for hospitals that are already customers."
                      data-testid="ord-approve-hospital-silent"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Approve Silently
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Manual welcome-email fallback — only shown after an approve that
              failed auto-delivery. mailto: link opens Outlook (or the admin's
              default mail client) pre-filled with the welcome letter. */}
          {manualWelcomeEmail && (
            <div className="rounded-lg border-l-4 border-amber-400 bg-amber-50 px-4 py-3 mb-4" data-testid="ord-manual-welcome-email">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-200 text-amber-800 flex items-center justify-center shrink-0 mt-0.5">
                  <Mail className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-amber-900">Send the welcome email manually</p>
                  <p className="text-[12px] text-amber-800 mt-0.5 leading-snug">
                    Auto-delivery to <strong>{manualWelcomeEmail.recipient}</strong> didn&apos;t reach the inbox
                    {manualWelcomeEmail.detail ? <> (<span className="italic">{manualWelcomeEmail.detail.slice(0, 80)}{manualWelcomeEmail.detail.length > 80 ? "…" : ""}</span>)</> : null}.
                    Click below to open it in Outlook, pre-filled and ready to send from <strong>orders@sterilemate.com</strong>.
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <button
                      onClick={() => openManualEmailInOutlook(manualWelcomeEmail)}
                      className="px-3 py-1.5 text-xs font-bold rounded-md bg-amber-600 hover:bg-amber-700 text-white inline-flex items-center gap-1.5"
                      data-testid="ord-open-outlook"
                    >
                      <Mail className="w-3 h-3" /> Send via Outlook
                    </button>
                    <button
                      onClick={() => { navigator.clipboard.writeText(manualWelcomeEmail.plain_body || ""); toast.success("Email body copied"); }}
                      className="px-2.5 py-1.5 text-xs font-semibold rounded-md bg-white border border-amber-300 text-amber-900 hover:bg-amber-100"
                    >
                      Copy body
                    </button>
                    <button
                      onClick={() => setManualWelcomeEmail(null)}
                      className="text-xs text-amber-700 hover:text-amber-900 ml-auto"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "details" && (
            <div className="space-y-4">
              {/* Items */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <Package className="w-3.5 h-3.5" /> Items ({totalLines})
                  </h3>
                  <button
                    type="button"
                    onClick={addOrderItem}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:text-amber-900 hover:bg-amber-50 border border-amber-300 rounded px-2 py-1"
                    data-testid="ord-drawer-add-item"
                    title="Add a manual line item to this order"
                  >
                    <Plus className="w-3 h-3" /> Add item
                  </button>
                </div>
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-1.5">Item</th>
                        <th className="text-left px-3 py-1.5 w-24">SKU</th>
                        <th className="text-right px-3 py-1.5">Qty</th>
                        <th className="text-right px-3 py-1.5">Unit</th>
                        <th className="text-right px-3 py-1.5 text-amber-700" title="Your cost — admin-only, not on the customer PDF">🔒 Internal</th>
                        <th className="text-right px-3 py-1.5 text-amber-700" title="Markup % — admin-only">Markup</th>
                        <th className="text-right px-3 py-1.5">Line</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(draft.items || []).map((it, i) => {
                        const sub = it.substitute && (it.substitute.name || it.substitute.sku) ? it.substitute : null;
                        // Manual customer orders store `unit_cost`; backorder quotes store `unit_price`.
                        // Accept either so the drawer renders both shapes correctly.
                        const itPrice = it.unit_price ?? it.unit_cost ?? null;
                        const itLine = it.line_total ?? (itPrice != null ? itPrice * it.quantity : null);
                        const subPrice = sub ? (sub.unit_price ?? sub.unit_cost ?? null) : null;
                        return (
                          <Fragment key={i}>
                            <tr>
                              <td className="px-3 py-2">
                                <div className="flex items-start gap-2.5">
                                  {it.image_url && (
                                    <img
                                      src={it.image_url}
                                      alt=""
                                      className="w-12 h-12 rounded border border-slate-200 object-cover bg-slate-50 flex-shrink-0"
                                      data-testid={`ord-item-image-${i}`}
                                    />
                                  )}
                                  <div className="min-w-0 w-full">
                                    {editing ? (
                                      <>
                                        <input
                                          value={it.name || ""}
                                          onChange={(e) => setItemField(i, "name", e.target.value)}
                                          placeholder="Item name"
                                          className="w-full bg-white border border-slate-300 rounded px-1.5 py-0.5 text-sm font-medium"
                                          data-testid={`ord-item-name-${i}`}
                                        />
                                        <input
                                          value={it.description || ""}
                                          onChange={(e) => setItemField(i, "description", e.target.value)}
                                          placeholder="Description (optional)"
                                          className="w-full mt-1 bg-white border border-slate-300 rounded px-1.5 py-0.5 text-[11px] text-slate-600"
                                          data-testid={`ord-item-desc-input-${i}`}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <div className="font-medium text-slate-900">{it.name}</div>
                                        {it.description && (
                                          <div className="text-[11px] text-slate-500 mt-0.5 leading-snug line-clamp-2" data-testid={`ord-item-desc-${i}`}>
                                            {it.description}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                  {editing && (
                                    <button
                                      type="button"
                                      onClick={() => removeOrderItem(i)}
                                      className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded p-1 flex-shrink-0"
                                      title="Remove this item"
                                      data-testid={`ord-item-remove-${i}`}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2 font-mono text-xs text-slate-600 align-top">
                                {editing ? (
                                  <input
                                    value={it.sku || ""}
                                    onChange={(e) => setItemField(i, "sku", e.target.value)}
                                    placeholder="SKU"
                                    className="w-24 bg-white border border-slate-300 rounded px-1.5 py-0.5 text-xs font-mono"
                                    data-testid={`ord-item-sku-${i}`}
                                  />
                                ) : (it.sku || it.mfr_number || "—")}
                              </td>
                              <td className="px-3 py-2 text-right font-mono align-top">
                                {editing ? (
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={it.quantity ?? 0}
                                    onChange={(e) => setItemField(i, "quantity", e.target.value)}
                                    className="w-16 bg-white border border-slate-300 rounded px-1.5 py-0.5 text-sm text-right"
                                    data-testid={`ord-item-qty-${i}`}
                                  />
                                ) : it.quantity}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums align-top">
                                {editing ? (
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={itPrice ?? 0}
                                    onChange={(e) => setItemField(i, "unit_price", e.target.value)}
                                    className="w-20 bg-white border border-slate-300 rounded px-1.5 py-0.5 text-sm text-right"
                                    data-testid={`ord-item-price-${i}`}
                                  />
                                ) : (itPrice != null ? fmt$(itPrice) : "—")}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums align-top text-amber-700 font-semibold" data-testid={`ord-item-internal-${i}`}>
                                {editing ? (
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={it.internal_cost ?? ""}
                                    onChange={(e) => setItemField(i, "internal_cost", e.target.value)}
                                    placeholder="cost"
                                    className="w-20 bg-white border border-amber-300 rounded px-1.5 py-0.5 text-sm text-right"
                                    data-testid={`ord-item-internal-input-${i}`}
                                  />
                                ) : (it.internal_cost != null ? fmt$(it.internal_cost) : "—")}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums align-top text-xs" data-testid={`ord-item-markup-${i}`}>
                                {(() => {
                                  const ic = Number(it.internal_cost);
                                  const uc = Number(itPrice);
                                  if (!ic || !uc || ic <= 0) return <span className="text-slate-300">—</span>;
                                  const pct = ((uc - ic) / ic) * 100;
                                  const color = pct >= 30 ? "text-emerald-700" : pct >= 10 ? "text-amber-700" : "text-rose-700";
                                  return <span className={`font-bold ${color}`}>{pct.toFixed(1)}%</span>;
                                })()}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums font-bold align-top">{itLine != null ? fmt$(itLine) : "—"}</td>
                            </tr>
                            {sub && (
                              <tr className="bg-amber-50/60">
                                <td className="px-3 py-1.5 pl-6 text-xs">
                                  <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 mr-1.5">↳ Sub</span>
                                  <span className="text-slate-800">{sub.name || "(substitute)"}</span>
                                  {sub.notes && <div className="text-[11px] text-amber-700 italic mt-0.5">{sub.notes}</div>}
                                </td>
                                <td className="px-3 py-1.5 font-mono text-xs text-amber-800">{sub.sku || "—"}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-xs text-amber-800">{sub.quantity || it.quantity}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-xs text-amber-800">{subPrice != null ? fmt$(subPrice) : "—"}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-xs text-amber-800">—</td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-xs text-amber-800">—</td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-xs text-amber-800">{subPrice != null ? fmt$(subPrice * (sub.quantity || it.quantity || 0)) : "—"}</td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-slate-50 text-xs">
                      <tr><td colSpan={6} className="px-3 py-1.5 text-right text-slate-500">Subtotal</td><td className="px-3 py-1.5 text-right font-bold" data-testid="ord-subtotal-display">{fmt$(editing ? liveSubtotal : (draft.subtotal ?? draft.items_subtotal))}</td></tr>
                      {editing ? (
                        <tr className="bg-amber-50/40">
                          <td colSpan={7} className="px-3 py-3">
                            <div className="flex flex-wrap items-center justify-end gap-3">
                              <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer" data-testid="ord-freight-courier-label">
                                <input
                                  type="checkbox"
                                  checked={!!draft.use_hospital_courier}
                                  onChange={(e) => setDraft({ ...draft, use_hospital_courier: e.target.checked })}
                                  data-testid="ord-freight-courier-toggle"
                                />
                                Hospital 3rd-party courier
                              </label>
                              {draft.use_hospital_courier && (
                                <Input
                                  placeholder="UPS / FedEx account #"
                                  value={draft.hospital_courier_account || ""}
                                  onChange={(e) => setDraft({ ...draft, hospital_courier_account: e.target.value })}
                                  className="h-8 w-48 text-xs"
                                  data-testid="ord-freight-courier-account"
                                />
                              )}
                              <label className="text-xs font-semibold text-slate-600">Freight $</label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                disabled={draft.use_hospital_courier}
                                value={draft.freight_cost ?? ""}
                                onChange={(e) => setDraft({ ...draft, freight_cost: e.target.value })}
                                placeholder="0.00"
                                className="h-8 w-28 text-right text-xs disabled:bg-slate-100"
                                data-testid="ord-freight-input"
                              />
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <tr><td colSpan={6} className="px-3 py-1.5 text-right text-slate-500">Freight {draft.use_hospital_courier ? "(hospital-provided)" : (draft.source === "manual" ? "" : "(20%)")}</td><td className="px-3 py-1.5 text-right">{draft.use_hospital_courier ? <span className="text-slate-400 italic">—</span> : <span className="font-semibold" data-testid="ord-freight-display">{fmt$(draft.freight_cost)}</span>}</td></tr>
                      )}
                      <tr className="border-t border-slate-200"><td colSpan={6} className="px-3 py-2 text-right font-bold text-slate-900">Total</td><td className="px-3 py-2 text-right font-extrabold text-emerald-700 text-base" data-testid="ord-total-display">{fmt$(
                        (() => {
                          const sub = parseFloat(editing ? liveSubtotal : (draft.subtotal ?? draft.items_subtotal ?? 0)) || 0;
                          if (draft.use_hospital_courier) return sub;
                          const fr = parseFloat(draft.freight_cost ?? 0) || 0;
                          return sub + fr;
                        })()
                      )}</td></tr>
                    </tfoot>
                  </table>
                </div>
              </section>

              {seeCost && (() => {
                const g = orderGp(draft);
                return (
                  <section className="rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="ord-gp-panel">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-amber-800">
                          🔒 Cost &amp; gross profit — back office only
                        </p>
                        <p className="text-[11px] text-amber-800/80 mt-0.5">
                          Never printed on the customer PDF, emailed, or returned to the hospital portal.
                          Edit a unit price or an internal cost above and this recalculates.
                        </p>
                      </div>
                      {draft.supplier_po_number && (
                        <span className="text-[11px] font-mono font-bold text-amber-900 bg-white border border-amber-200 rounded px-2 py-1">
                          Supplier PO {draft.supplier_po_number}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                      {[["Revenue billed", fmt$(g.revenue), "text-slate-900", "ord-gp-revenue"],
                        ["Supplier cost", fmt$(g.cost), "text-amber-800", "ord-gp-cost"],
                        ["Gross profit", fmt$(g.gp), "text-emerald-700", "ord-gp-amount"],
                        ["GP %", g.gpPct == null ? "—" : `${g.gpPct}%`,
                          g.gpPct == null ? "text-slate-400" : g.gpPct >= 25 ? "text-emerald-700" : g.gpPct >= 12 ? "text-amber-700" : "text-rose-700",
                          "ord-gp-pct"]].map(([label, value, tone, tid]) => (
                        <div key={label} className="rounded-lg bg-white border border-amber-200 px-3 py-2" data-testid={tid}>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
                          <p className={`text-lg font-black tabular-nums ${tone}`}>{value}</p>
                        </div>
                      ))}
                    </div>
                    {g.missing > 0 && (
                      <p className="text-[12px] font-semibold text-rose-700 mt-2.5" data-testid="ord-gp-missing">
                        {g.missing} line{g.missing === 1 ? "" : "s"} have no supplier cost — this margin is
                        overstated until you enter 🔒 Internal $ above.
                      </p>
                    )}
                  </section>
                );
              })()}

              <section className="grid sm:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Contact</h3>
                  <div className="text-sm text-slate-700 space-y-0.5">
                    <div className="font-semibold">{draft.contact_name || "—"}</div>
                    <div><a className="text-blue-600 underline" href={`mailto:${draft.contact_email}`}>{draft.contact_email}</a></div>
                    <div>{draft.contact_phone || "—"}</div>
                  </div>
                </div>
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Ship-to</h3>
                  <Textarea
                    rows={2}
                    disabled={!editing}
                    value={draft.shipping_address || ""}
                    onChange={(e) => setDraft({ ...draft, shipping_address: e.target.value })}
                    className="text-sm disabled:bg-slate-50"
                  />
                </div>
              </section>

              {draft.notes && (
                <section>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Customer Notes</h3>
                  <div className="bg-amber-50/60 border border-amber-200 rounded p-3 text-sm whitespace-pre-wrap text-slate-700">{draft.notes}</div>
                </section>
              )}

              <section>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Internal Admin Notes</h3>
                <Textarea
                  rows={3}
                  disabled={!editing}
                  value={draft.admin_notes || ""}
                  onChange={(e) => setDraft({ ...draft, admin_notes: e.target.value })}
                  placeholder="Internal notes (not visible to hospital)…"
                  className="text-sm disabled:bg-slate-50"
                />
              </section>
            </div>
          )}

          {tab === "ship" && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">PO Number</label>
                  <Input disabled={!editing} value={draft.po_number || ""} onChange={(e) => setDraft({ ...draft, po_number: e.target.value })} placeholder="PO-2026-001" data-testid="ord-po" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Payment Terms</label>
                  <select
                    disabled={!editing}
                    value={draft.payment_terms || "Net 30"}
                    onChange={(e) => setDraft({ ...draft, payment_terms: e.target.value })}
                    className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 disabled:bg-slate-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-amber-500"
                    data-testid="ord-payment-terms"
                  >
                    <option value="Net 30">Net 30</option>
                    <option value="Prepay">Prepay</option>
                    <option value="Net 15">Net 15</option>
                    <option value="Net 45">Net 45</option>
                    <option value="Net 60">Net 60</option>
                    <option value="COD">COD</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Carrier</label>
                  <Input disabled={!editing} value={draft.carrier || ""} onChange={(e) => setDraft({ ...draft, carrier: e.target.value })} placeholder="FedEx / UPS / DHL" data-testid="ord-carrier" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Tracking #</label>
                  <Input disabled={!editing} value={draft.tracking_number || ""} onChange={(e) => setDraft({ ...draft, tracking_number: e.target.value })} placeholder="1Z999…" data-testid="ord-tracking" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Expected Delivery</label>
                  <Input disabled={!editing} type="date" value={(draft.expected_delivery || "").slice(0,10)} onChange={(e) => setDraft({ ...draft, expected_delivery: e.target.value })} data-testid="ord-eta" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Shipped At</label>
                  <Input disabled value={draft.shipped_at ? fmtTs(draft.shipped_at) : "—"} className="bg-slate-50" />
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <label className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    disabled={!editing}
                    checked={!!draft.use_hospital_courier}
                    onChange={(e) => setDraft({ ...draft, use_hospital_courier: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300"
                  />
                  <div className="flex-1">
                    <span className="font-semibold text-slate-900">Hospital provides its own freight forwarder</span>
                    {draft.hospital_courier_account && (
                      <div className="text-xs text-slate-500 mt-1">{draft.hospital_courier_account}</div>
                    )}
                  </div>
                </label>
                {editing && draft.use_hospital_courier && (
                  <Input
                    className="mt-2"
                    placeholder="Carrier — Acct # — Contact"
                    value={draft.hospital_courier_account || ""}
                    onChange={(e) => setDraft({ ...draft, hospital_courier_account: e.target.value })}
                  />
                )}
              </div>

              {/* One-click "Mark As Shipped from PO" — auto-posts a Shipped
                  milestone using the carrier/tracking/ETA values already on
                  this PO record so admins don't retype anything. */}
              <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center">
                      <Truck className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-emerald-900">Mark As Shipped from PO</div>
                      <div className="text-xs text-emerald-800 mt-0.5">
                        Auto-creates a Shipped milestone using the carrier + tracking number above and emails the hospital.
                      </div>
                      {(!draft.tracking_number || !draft.carrier) && (
                        <div className="text-[11px] text-amber-700 mt-1 font-semibold">
                          ⚠ Add a Carrier + Tracking # above first, then click below.
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={async () => {
                      if (!draft.tracking_number?.trim() || !draft.carrier?.trim()) {
                        toast.error("Add a Carrier and Tracking # in the fields above first");
                        return;
                      }
                      try {
                        const payload = {
                          type: "shipped",
                          courier: draft.carrier.trim(),
                          tracking_number: draft.tracking_number.trim(),
                          estimated_arrival: draft.expected_delivery
                            ? draft.expected_delivery.slice(0, 10)
                            : null,
                          note: draft.po_number
                            ? `Shipped under PO ${draft.po_number}`
                            : "Shipped from PO",
                        };
                        await axios.post(
                          `${API}/api/admin/orders/${order.id}/milestones`,
                          payload,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        toast.success("Shipped — hospital notified");
                        onChanged?.();
                        setTab("fulfillment");
                      } catch (e) {
                        toast.error(
                          e?.response?.data?.detail || "Failed to post milestone"
                        );
                      }
                    }}
                    disabled={!draft.tracking_number?.trim() || !draft.carrier?.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
                    data-testid="ord-mark-shipped-from-po"
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    Mark As Shipped &amp; Email Hospital
                  </Button>
                </div>
              </div>
            </div>
          )}

          {tab === "fulfillment" && (
            <BackorderFulfillmentTab
              backorder={order}
              token={token}
              onRefresh={onChanged}
            />
          )}

          {tab === "comms" && (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Log new communication</h4>
                <div className="grid grid-cols-3 gap-2">
                  <select className="text-sm border border-slate-300 rounded px-2 py-1.5" value={newComm.channel} onChange={(e) => setNewComm({ ...newComm, channel: e.target.value })}>
                    <option value="email">Email</option>
                    <option value="call">Call</option>
                    <option value="sms">SMS</option>
                    <option value="meeting">Meeting</option>
                  </select>
                  <select className="text-sm border border-slate-300 rounded px-2 py-1.5" value={newComm.direction} onChange={(e) => setNewComm({ ...newComm, direction: e.target.value })}>
                    <option value="out">Outbound</option>
                    <option value="in">Inbound</option>
                  </select>
                  <Input placeholder="Contact name" value={newComm.contact} onChange={(e) => setNewComm({ ...newComm, contact: e.target.value })} className="h-9" />
                </div>
                <Textarea rows={2} placeholder="What did you discuss / agree on?" value={newComm.summary} onChange={(e) => setNewComm({ ...newComm, summary: e.target.value })} data-testid="ord-new-comm-summary" />
                <Button size="sm" onClick={addComm} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="ord-add-comm">
                  <Send className="w-3.5 h-3.5" /> Log
                </Button>
              </div>

              <div className="space-y-2">
                {((draft.communications || []).slice().reverse()).map((c, i) => (
                  <div key={i} className="border border-slate-200 rounded p-3 bg-white">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${c.direction === "in" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>
                          {c.direction === "in" ? "← In" : "→ Out"}
                        </span>
                        <span className="font-semibold text-slate-700 capitalize">{c.channel}</span>
                        {c.contact && <span className="text-slate-400">·</span>}
                        {c.contact && <span className="text-slate-600">{c.contact}</span>}
                      </div>
                      <span className="text-[11px] text-slate-400">{fmtTs(c.ts)}</span>
                    </div>
                    <div className="text-sm text-slate-700">{c.summary}</div>
                    <div className="text-[10px] text-slate-400 mt-1">by {c.by}</div>
                  </div>
                ))}
                {(!draft.communications || draft.communications.length === 0) && (
                  <p className="text-sm text-slate-400 italic">No communications logged yet.</p>
                )}
              </div>
            </div>
          )}

          {tab === "notes" && (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Add progress note</h4>
                <Textarea
                  rows={2}
                  placeholder="e.g., Called manufacturer, ETA confirmed for Friday…"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  data-testid="ord-new-note"
                />
                <Button size="sm" onClick={addNote} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="ord-add-note">
                  <Plus className="w-3.5 h-3.5" /> Add Note
                </Button>
              </div>

              <div className="space-y-2">
                {((draft.progress_notes || []).slice().reverse()).map((n, i) => (
                  <div key={i} className="border border-slate-200 rounded p-3 bg-white">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${n.kind === "status" ? "bg-violet-50 text-violet-700" : "bg-amber-50 text-amber-700"}`}>
                        {n.kind === "status" ? "Status change" : "Note"}
                      </span>
                      <span className="text-[11px] text-slate-400">{fmtTs(n.ts)}</span>
                    </div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap">{n.text}</div>
                    <div className="text-[10px] text-slate-400 mt-1">by {n.by}</div>
                  </div>
                ))}
                {(!draft.progress_notes || draft.progress_notes.length === 0) && (
                  <p className="text-sm text-slate-400 italic">No progress notes yet.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// NewCustomerOrderInline
// ----------------------------------------------------------------------------
// Bottom-sheet modal for creating a manual customer order from the Orders
// Window. Captures the same fields the notification engine uses:
//   • Hospital + contact (name + email)
//   • Line items: name, SKU, qty, UOM, unit cost  (auto-computes line total)
//   • Customer PO #, freight cost, payment terms
//   • Initial status (defaults to "new")
//   • Optional "Save & Send Quote" — creates + immediately emails the quote.
// ============================================================================
function NewCustomerOrderInline({ open, onClose, onSuccess, token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [meta, setMeta] = useState({
    hospital_name: "",
    hospital_id: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    customer_po_number: "",
    supplier_name: "",
    supplier_email: "",
    supplier_po_number: "",
    supplier_not_assigned: false,
    payment_terms: "Net 30",
    freight_cost: "",
    admin_notes: "",
    status: "new",
  });
  const [items, setItems] = useState([
    { name: "", sku: "", quantity: 1, uom: "Each", unit_cost: "" },
  ]);
  const [submitting, setSubmitting] = useState(false);

  // Hospital autocomplete — pull from /api/backorders/hospitals on open. When
  // a row is picked we auto-fill contact_name + contact_email from the CRM.
  // Typing a name that doesn't match still creates a brand-new hospital.
  const [hospitalDb, setHospitalDb] = useState([]);
  const [showHospitalSuggest, setShowHospitalSuggest] = useState(false);
  const [pickedHospital, setPickedHospital] = useState(null);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axios
      .get(`${API}/api/backorders/hospitals`, { headers })
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : r.data?.hospitals || r.data?.items || [];
        setHospitalDb(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const hospitalMatches = useMemo(() => {
    const q = (meta.hospital_name || "").trim().toLowerCase();
    if (!q || pickedHospital) return [];
    return hospitalDb
      .filter((h) => (h.hospital_name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [meta.hospital_name, hospitalDb, pickedHospital]);
  const acceptHospital = (h) => {
    setPickedHospital(h);
    setMeta((m) => ({
      ...m,
      hospital_name: h.hospital_name || "",
      hospital_id: h.account_id || h.id || "",
      contact_name: h.contact_name || m.contact_name,
      contact_email: h.contact_email || m.contact_email,
      contact_phone: h.phone || m.contact_phone,
    }));
    setShowHospitalSuggest(false);
  };
  const clearHospital = () => {
    setPickedHospital(null);
    setMeta((m) => ({ ...m, hospital_name: "", hospital_id: "", contact_name: "", contact_email: "", contact_phone: "" }));
  };
  // Per-hospital pricing memory — when the admin types a hospital name,
  // we look up that customer's past unit_costs by SKU so the picker can
  // surface "Last paid $0.25 ×3 orders" badges and prefill the customer-
  // specific price when an SKU is added. Falls back to catalog default if
  // we have no history for that SKU.
  const [hospitalPricing, setHospitalPricing] = useState({}); // sku → { last_price, last_ts, times_purchased, last_uom }
  // Category list for the per-line-item Category select. Fetched once on mount.
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    axios
      .get(`${API}/api/admin/orders/marketplace-categories`, { headers })
      .then((r) => setCategories(r.data?.categories || []))
      .catch(() => setCategories([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const h = (meta.hospital_name || "").trim();
    if (h.length < 3) {
      setHospitalPricing({});
      return;
    }
    const tid = setTimeout(() => {
      axios
        .get(`${API}/api/admin/orders/customer-pricing-history`, {
          headers,
          params: { hospital_name: h },
        })
        .then((r) => {
          const idx = {};
          for (const row of r.data?.items || []) idx[row.sku] = row;
          setHospitalPricing(idx);
        })
        .catch(() => setHospitalPricing({}));
    }, 450);
    return () => clearTimeout(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.hospital_name]);
  // Marketplace picker — searchable index of janitorial + American Surgical
  // catalog SKUs (client-side), merged with DI Tank + Aspen Surgical from a
  // backend endpoint on first open. Clicking a row appends a new line with
  // name/SKU/UOM/cost pre-filled. Every field is still freely editable.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addProductOpen, setAddProductOpen] = useState(false);  // 2-tab dialog (Manual + CSV)
  const [pickerSearch, setPickerSearch] = useState("");
  const [backendIndex, setBackendIndex] = useState([]);
  const [backendIndexLoading, setBackendIndexLoading] = useState(false);
  const clientIndex = useMemo(buildMarketplaceIndex, []);
  const marketplaceIndex = useMemo(
    () => [...clientIndex, ...backendIndex],
    [clientIndex, backendIndex]
  );

  // Fetch backend-resident catalogs (DI Tank, Aspen Surgical) the first time
  // the picker is opened. Cached for the lifetime of the modal.
  useEffect(() => {
    if (!pickerOpen || backendIndex.length || backendIndexLoading) return;
    setBackendIndexLoading(true);
    axios
      .get(`${API}/api/admin/orders/marketplace/sku-index`, { headers })
      .then((r) => setBackendIndex(r.data?.items || []))
      .catch(() => toast.error("Could not load extended catalog"))
      .finally(() => setBackendIndexLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen]);

  const pickerResults = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return marketplaceIndex.slice(0, 40);
    return marketplaceIndex
      .filter((p) => `${p.sku} ${p.name} ${p.category}`.toLowerCase().includes(q))
      .slice(0, 60);
  }, [marketplaceIndex, pickerSearch]);

  const pickFromMarketplace = (p) => {
    // Customer-specific pricing memory — if we've sold this SKU to this
    // hospital before, prefer that "last paid" price over the catalog default.
    const memory = hospitalPricing[p.sku];
    const finalUnitCost = memory?.last_price != null
      ? String(memory.last_price)
      : (p.unit_cost != null ? String(p.unit_cost) : "");
    const finalUom = memory?.last_uom || p.uom || "Each";
    setItems((prev) => [
      ...prev,
      {
        name: p.name,
        sku: p.sku,
        quantity: 1,
        uom: finalUom,
        unit_cost: finalUnitCost,
        image_url: p.image || null,
        description: p.description || null,
      },
    ]);
    toast.success(
      memory
        ? `Added ${p.sku} at ${meta.hospital_name}'s last price ($${memory.last_price})`
        : `Added ${p.sku} — edit any field as needed`
    );
    setPickerOpen(false);
    setPickerSearch("");
  };

  if (!open) return null;

  const setIt = (i, k, v) =>
    setItems((p) => p.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const addItem = () =>
    setItems((p) => [...p, { name: "", sku: "", quantity: 1, uom: "Each", unit_cost: "", internal_cost: "", category: "", supplier: "" }]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const itemsSubtotal = items.reduce((acc, it) => {
    const c = parseFloat(it.unit_cost);
    const q = parseInt(it.quantity) || 0;
    return acc + (isNaN(c) ? 0 : c * q);
  }, 0);
  const freightNum = parseFloat(meta.freight_cost) || 0;
  const grandTotal = itemsSubtotal + freightNum;

  const submit = async ({ andSendQuote = false } = {}) => {
    if (!meta.hospital_name.trim()) return toast.error("Hospital / customer name is required");
    if (!meta.contact_email.includes("@")) return toast.error("Valid contact email is required");
    const valid = items.filter((it) => (it.name || "").trim());
    if (!valid.length) return toast.error("At least one line item is required");
    // Our Supplier PO means the supplier is committed, so the cost we pay has
    // to ride along — it drives the payables PO and the gross profit report.
    if (meta.supplier_po_number.trim()) {
      const noCost = valid.filter((it) => it.internal_cost === "" || it.internal_cost == null);
      if (noCost.length) {
        return toast.error(
          `Enter the supplier cost (🔒 Internal $) for: ${noCost.map((it) => it.name).slice(0, 3).join(", ")}` +
          `${noCost.length > 3 ? ` +${noCost.length - 3} more` : ""}`,
          { description: "It is what we pay the supplier and it drives the gross profit report." }
        );
      }
    }

    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/orders/manual`,
        {
          hospital_name: meta.hospital_name.trim(),
          hospital_id: meta.hospital_id || null,
          contact_name: meta.contact_name || "—",
          contact_email: meta.contact_email.trim().toLowerCase(),
          contact_phone: meta.contact_phone || null,
          customer_po_number: meta.customer_po_number || null,
          supplier_name: meta.supplier_name || null,
          supplier_email: meta.supplier_email || null,
          supplier_po_number: meta.supplier_po_number || null,
          supplier_not_assigned: !!meta.supplier_not_assigned,
          payment_terms: meta.payment_terms,
          freight_cost: meta.freight_cost === "" ? null : meta.freight_cost,
          admin_notes: meta.admin_notes || null,
          status: meta.status,
          items: valid.map((it) => ({
            name: it.name.trim(),
            sku: it.sku || null,
            quantity: parseInt(it.quantity) || 1,
            uom: it.uom || "Each",
            unit_cost: it.unit_cost === "" ? null : parseFloat(it.unit_cost),
            internal_cost: it.internal_cost === "" || it.internal_cost == null ? null : parseFloat(it.internal_cost),
            category: it.category || null,
            supplier: it.supplier || null,
            image_url: it.image_url || null,
            description: it.description || null,
          })),
        },
        { headers }
      );
      const orderId = r.data?.order?.id;
      toast.success(`Customer order ${orderId} created`);
      if (andSendQuote && orderId) {
        try {
          await axios.post(
            `${API}/api/admin/orders/${orderId}/email-record`,
            { template: "quote_sent", to_email: meta.contact_email },
            { headers }
          );
          toast.success("Quote email sent to customer");
        } catch (qErr) {
          toast.error("Order saved but quote email failed");
        }
      }
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
      onClick={(e) => {
        // Only close when the click landed DIRECTLY on the backdrop, not on
        // a child or a portaled element (e.g. Radix Dialog backdrop, sonner
        // toast) that bubbles a synthetic click through React's event system.
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="orders-new-modal"
    >
      <div
        className="bg-white rounded-t-xl sm:rounded-xl shadow-2xl w-full max-w-3xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">New Customer Order</h2>
            <p className="text-xs text-slate-500">
              Build a live customer order &mdash; quote, line items, freight, terms.
              The drawer afterward lets you process, ship, track, and send notifications.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="orders-new-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Customer */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Hospital / Customer *</label>
              {pickedHospital ? (
                <div className="rounded border border-emerald-300 bg-emerald-50/60 p-2 flex items-start justify-between gap-2" data-testid="co-form-picked-hospital">
                  <div className="text-sm min-w-0 flex-1">
                    <div className="font-bold text-slate-900 truncate">{pickedHospital.hospital_name}</div>
                    <div className="text-xs text-slate-600 truncate">
                      {pickedHospital.contact_name || "—"} · {pickedHospital.contact_email || "no email on file"}
                      {pickedHospital.phone ? ` · ☎ ${pickedHospital.phone}` : ""}
                    </div>
                    {pickedHospital.account_id && (
                      <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                        ✓ from {pickedHospital.source === "crm" ? "CRM" : (pickedHospital.source === "health_systems" ? "Health Systems" : "Portal")}
                      </span>
                    )}
                  </div>
                  <button type="button" onClick={clearHospital} className="text-rose-500 hover:text-rose-700" data-testid="co-form-clear-hospital">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Input
                    value={meta.hospital_name}
                    onChange={(e) => { setMeta({ ...meta, hospital_name: e.target.value }); setShowHospitalSuggest(true); }}
                    onFocus={() => setShowHospitalSuggest(true)}
                    onBlur={() => setTimeout(() => setShowHospitalSuggest(false), 180)}
                    placeholder="Start typing to search existing customers — or enter a brand-new hospital name"
                    autoComplete="off"
                    data-testid="co-form-hospital"
                  />
                  {showHospitalSuggest && hospitalMatches.length > 0 && (
                    <ul className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-xl max-h-64 overflow-y-auto" data-testid="co-form-hospital-suggest">
                      {hospitalMatches.map((h, idx) => (
                        <li key={`${h.account_id || h.hospital_name}-${idx}`}>
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => acceptHospital(h)}
                            className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-slate-100 last:border-b-0 flex items-start justify-between gap-3"
                            data-testid={`co-form-hospital-suggest-${idx}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-slate-900 truncate">{h.hospital_name}</div>
                              <div className="text-[11px] text-slate-500 truncate">
                                {h.contact_name || "—"} · {h.contact_email || "no email on file"}
                                {h.phone ? ` · ☎ ${h.phone}` : ""}
                              </div>
                            </div>
                            {h.account_id && (
                              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 shrink-0 mt-0.5">
                                {h.source === "crm" ? "CRM" : (h.source === "health_systems" ? "HS" : "Portal")}
                              </span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-[11px] text-slate-500 mt-1">
                    Type to search the CRM &mdash; pick a row to auto-fill contact info, or just type a new hospital name to create one.
                  </p>
                </div>
              )}
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Contact name</label>
              <Input value={meta.contact_name} onChange={(e) => setMeta({ ...meta, contact_name: e.target.value })} placeholder="Jane Procurement" data-testid="co-form-contact-name" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Contact email *</label>
              <Input type="email" value={meta.contact_email} onChange={(e) => setMeta({ ...meta, contact_email: e.target.value })} placeholder="jane@hospital.com" data-testid="co-form-contact-email" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Contact phone</label>
              <Input type="tel" value={meta.contact_phone} onChange={(e) => setMeta({ ...meta, contact_phone: e.target.value })} placeholder="(555) 123-4567" data-testid="co-form-contact-phone" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Customer PO #</label>
              <Input value={meta.customer_po_number} onChange={(e) => setMeta({ ...meta, customer_po_number: e.target.value })} placeholder="MH-PO-12345" data-testid="co-form-po" />
              <p className="text-[10px] text-slate-400 mt-0.5">The hospital's PO to us.</p>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Supplier</label>
              <Input value={meta.supplier_name} onChange={(e) => setMeta({ ...meta, supplier_name: e.target.value })} placeholder="Luxe Medical Group Inc" data-testid="co-form-supplier-name" />
              <Input className="mt-1" value={meta.supplier_email} onChange={(e) => setMeta({ ...meta, supplier_email: e.target.value })} placeholder="ap@supplier.com" data-testid="co-form-supplier-email" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Our Supplier PO # *</label>
              <Input value={meta.supplier_po_number} disabled={meta.supplier_not_assigned}
                onChange={(e) => setMeta({ ...meta, supplier_po_number: e.target.value })}
                placeholder="SM-PO-10045" data-testid="co-form-supplier-po" />
              <label className="flex items-center gap-1.5 text-[10.5px] text-slate-600 mt-1">
                <input type="checkbox" checked={meta.supplier_not_assigned} data-testid="co-form-supplier-unassigned"
                  onChange={(e) => setMeta({ ...meta, supplier_not_assigned: e.target.checked, supplier_po_number: e.target.checked ? "" : meta.supplier_po_number })} />
                Supplier not assigned yet
              </label>
              <p className="text-[10px] text-slate-400 mt-0.5">
                Opens the PO in the payables ledger at our supplier cost, so every supplier
                invoice matches back to it.
              </p>
              {meta.supplier_po_number.trim() && (
                <p className="text-[10px] font-bold text-amber-700 mt-1" data-testid="co-form-cost-required-hint">
                  🔒 Internal $ is required on every line while this PO is set.
                </p>
              )}
            </div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Line items</p>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPickerOpen(true)}
                  className="gap-1 h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="co-form-add-from-marketplace"
                  title="Pick from existing marketplace catalog (janitorial, American Surgical, etc.)"
                >
                  <Package className="w-3 h-3" /> Add from Marketplace
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setAddProductOpen(true)}
                  className="gap-1 h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  data-testid="co-form-add-manual-csv"
                  title="Build one product (with image upload) OR paste a CSV of many products. Optional 'Also save to marketplace' makes them reusable."
                >
                  <Plus className="w-3 h-3" /> Manual / CSV
                </Button>
                <Button size="sm" variant="outline" onClick={addItem} className="gap-1 h-7 text-xs" data-testid="co-form-add-item">
                  <Plus className="w-3 h-3" /> Add blank row
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => {
                const lineTotal =
                  it.unit_cost && it.quantity
                    ? (parseFloat(it.unit_cost) * (parseInt(it.quantity) || 0)).toFixed(2)
                    : null;
                return (
                  <div key={i} className="space-y-1.5">
                    <div className="grid grid-cols-12 gap-2 bg-slate-50 border border-slate-200 rounded p-2" data-testid={`co-form-item-row-${i}`}>
                      <Input className="col-span-12 sm:col-span-4 h-8 text-sm" placeholder="Item name *" value={it.name || ""} onChange={(e) => setIt(i, "name", e.target.value)} data-testid={`co-form-item-name-${i}`} />
                      <Input className="col-span-6 sm:col-span-2 h-8 text-sm font-mono" placeholder="SKU" value={it.sku || ""} onChange={(e) => setIt(i, "sku", e.target.value)} data-testid={`co-form-item-sku-${i}`} />
                      <Input className="col-span-3 sm:col-span-1 h-8 text-sm text-center" type="number" min="1" value={it.quantity ?? ""} onChange={(e) => setIt(i, "quantity", e.target.value)} data-testid={`co-form-item-qty-${i}`} />
                      <select className="col-span-3 sm:col-span-1 h-8 rounded border border-slate-300 bg-white text-sm px-1" value={it.uom || "Each"} onChange={(e) => setIt(i, "uom", e.target.value)} data-testid={`co-form-item-uom-${i}`}>
                        <option>Each</option><option>Case</option><option>Box</option><option>Pack</option><option>Pair</option><option>Bag</option><option>Roll</option><option>Bottle</option><option>Kit</option><option>Pallet</option>
                      </select>
                      <Input className="col-span-6 sm:col-span-2 h-8 text-sm text-right" type="number" step="0.01" min="0" placeholder="Unit $" value={it.unit_cost ?? ""} onChange={(e) => setIt(i, "unit_cost", e.target.value)} data-testid={`co-form-item-cost-${i}`} />
                      <div className="col-span-5 sm:col-span-1 h-8 px-2 flex items-center justify-end rounded bg-white border border-slate-200 text-xs font-bold text-slate-800" data-testid={`co-form-item-total-${i}`}>
                        {lineTotal ? `$${lineTotal}` : "—"}
                      </div>
                      <button onClick={() => removeItem(i)} className="col-span-1 text-rose-500 hover:text-rose-700 text-xs flex items-center justify-center" title="Remove line" data-testid={`co-form-item-remove-${i}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {/* Secondary admin-only row: Category + Internal Cost + markup% */}
                    <div className="grid grid-cols-12 gap-2 pl-2" data-testid={`co-form-item-meta-${i}`}>
                      <div className="col-span-12 sm:col-span-4 flex items-center gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 shrink-0">Category</span>
                        <select
                          className="flex-1 h-7 rounded border border-slate-200 bg-white text-xs px-1.5 text-slate-700"
                          value={it.category || ""}
                          onChange={(e) => setIt(i, "category", e.target.value)}
                          data-testid={`co-form-item-category-${i}`}
                        >
                          <option value="">— uncategorized —</option>
                          {categories.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-7 sm:col-span-4 flex items-center gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 shrink-0" title="Admin-only — never shown to customer">🔒 Internal $</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          className="flex-1 h-7 text-xs text-right border-amber-200"
                          placeholder="0.00"
                          value={it.internal_cost ?? ""}
                          onChange={(e) => setIt(i, "internal_cost", e.target.value)}
                          data-testid={`co-form-item-internal-cost-${i}`}
                        />
                      </div>
                      <div className="col-span-12 sm:col-span-4 flex items-center gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 shrink-0" title="Supplier name — powers per-supplier spend rollup">Supplier</span>
                        <Input
                          className="flex-1 h-7 text-xs"
                          placeholder="Vive Health"
                          value={it.supplier ?? ""}
                          onChange={(e) => setIt(i, "supplier", e.target.value)}
                          data-testid={`co-form-item-supplier-${i}`}
                        />
                      </div>
                      <div className="col-span-12 sm:col-span-12 flex items-center justify-end gap-1.5 -mt-1">
                        {(() => {
                          const ic = parseFloat(it.internal_cost);
                          const uc = parseFloat(it.unit_cost);
                          if (!ic || !uc || ic <= 0) return null;
                          const pct = ((uc - ic) / ic) * 100;
                          const color = pct >= 30 ? "text-emerald-700 bg-emerald-50 border-emerald-200" : pct >= 10 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-rose-700 bg-rose-50 border-rose-200";
                          return (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${color}`} data-testid={`co-form-item-markup-${i}`} title="Markup % — never shown to customer">
                              Markup {pct.toFixed(1)}%
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Freight + Terms + Status */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Freight ($)</label>
              <Input type="number" step="0.01" min="0" value={meta.freight_cost} onChange={(e) => setMeta({ ...meta, freight_cost: e.target.value })} className="text-right" data-testid="co-form-freight" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Payment terms</label>
              <select className="w-full h-9 rounded border border-slate-300 bg-white text-sm px-2" value={meta.payment_terms} onChange={(e) => setMeta({ ...meta, payment_terms: e.target.value })} data-testid="co-form-terms">
                <option>Net 30</option><option>Net 15</option><option>Net 45</option><option>Net 60</option><option>Due on Receipt</option><option>Prepaid</option><option>Credit Card</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Initial status</label>
              <select className="w-full h-9 rounded border border-slate-300 bg-white text-sm px-2" value={meta.status} onChange={(e) => setMeta({ ...meta, status: e.target.value })} data-testid="co-form-status">
                <option value="new">New</option><option value="quoted">Quoted</option><option value="po_received">PO Received</option><option value="in_fulfillment">In Fulfillment</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Internal notes <span className="text-slate-400 font-normal normal-case tracking-normal">— never shown to customer</span></label>
            <Textarea value={meta.admin_notes} onChange={(e) => setMeta({ ...meta, admin_notes: e.target.value })} rows={2} placeholder="Sourcing, markup math, vendor PO #, internal-only context…" data-testid="co-form-notes" />
          </div>

          {/* Totals preview */}
          <div className="bg-slate-50 rounded border border-slate-200 px-4 py-3 text-sm flex items-center justify-between">
            <div className="text-slate-600">
              Items <span className="font-bold text-slate-800">${itemsSubtotal.toFixed(2)}</span>
              <span className="mx-2 text-slate-300">+</span>
              Freight <span className="font-bold text-slate-800">${freightNum.toFixed(2)}</span>
            </div>
            <div className="text-lg font-bold text-slate-900" data-testid="co-form-grand-total">
              ${grandTotal.toFixed(2)}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex flex-wrap items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="co-form-cancel">
            Cancel
          </Button>
          <Button variant="outline" onClick={() => submit({ andSendQuote: false })} disabled={submitting} data-testid="co-form-save">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Draft"}
          </Button>
          <Button onClick={() => submit({ andSendQuote: true })} disabled={submitting} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="co-form-save-and-quote">
            <Send className="w-4 h-4" /> Save &amp; Email Quote
          </Button>
        </div>
      </div>

      {/* New 2-tab Add Products dialog (Manual Entry + CSV Import). Existing
          Marketplace picker stays below for the SKU-search + pricing-memory UX. */}
      <AddProductDialog
        open={addProductOpen}
        onClose={() => setAddProductOpen(false)}
        token={token}
        onAdd={(newItems) => {
          if (!newItems || newItems.length === 0) return;
          setItems((prev) => [...prev, ...newItems]);
          toast.success(`${newItems.length} item${newItems.length === 1 ? "" : "s"} added`);
        }}
      />

      {pickerOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => {
            e.stopPropagation();
            setPickerOpen(false);
          }}
          data-testid="co-marketplace-picker"
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900">Pick from Marketplace Catalog</h3>
                <p className="text-xs text-slate-500">
                  Janitorial + American Surgical SKUs. Click an item to add it as a line &mdash; you can still edit every field.
                </p>
              </div>
              <button onClick={() => setPickerOpen(false)} className="text-slate-400 hover:text-slate-600" data-testid="co-picker-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-3 border-b border-slate-200 space-y-2">
              <Input
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder="Search by SKU, name, or category (e.g. mwipe, 60-12, microfiber)…"
                autoFocus
                data-testid="co-picker-search"
              />
              {Object.keys(hospitalPricing).length > 0 && (
                <p className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1.5" data-testid="co-picker-pricing-banner">
                  <span>⭐</span>
                  Pricing memory active: <strong>{Object.keys(hospitalPricing).length}</strong> SKU(s) have history with {meta.hospital_name}. Their last paid price will be prefilled.
                </p>
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-2 py-2">
              {pickerResults.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-10">No matches for &ldquo;{pickerSearch}&rdquo;</p>
              ) : (
                pickerResults.map((p) => {
                  const memory = hospitalPricing[p.sku];
                  return (
                  <button
                    key={`${p.source}-${p.sku}`}
                    onClick={() => pickFromMarketplace(p)}
                    className={`w-full text-left p-2 rounded border mb-1 flex items-center gap-3 ${
                      memory
                        ? "border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50"
                        : "border-transparent hover:border-blue-200 hover:bg-blue-50"
                    }`}
                    data-testid={`co-picker-row-${p.sku}`}
                  >
                    {p.image && (
                      <img src={p.image} alt="" className="w-10 h-10 rounded object-cover bg-slate-100 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-slate-700">{p.sku}</span>
                        <span className="text-[10px] uppercase tracking-wider text-blue-600 font-bold">{p.source}</span>
                        {memory && (
                          <span
                            className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold bg-emerald-100 px-1.5 py-0.5 rounded"
                            data-testid={`co-picker-memory-${p.sku}`}
                            title={`Last paid $${memory.last_price} on order ${memory.last_order_id} · ${memory.times_purchased} order(s) total`}
                          >
                            ⭐ Your price
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-900 truncate">{p.name}</p>
                      {memory ? (
                        <p className="text-xs text-emerald-700 font-semibold">
                          Last paid ${memory.last_price} · {memory.times_purchased} order{memory.times_purchased > 1 ? "s" : ""}
                          {memory.last_ts ? ` · ${new Date(memory.last_ts).toLocaleDateString()}` : ""}
                        </p>
                      ) : p.price_label ? (
                        <p className="text-xs text-slate-500">{p.price_label}</p>
                      ) : null}
                    </div>
                    {(memory?.last_price != null || p.unit_cost != null) && (
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-slate-500">{memory ? "Your unit" : "Unit"}</p>
                        <p className={`text-sm font-bold ${memory ? "text-emerald-700" : "text-slate-700"}`}>
                          ${(memory?.last_price ?? p.unit_cost).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </button>
                  );
                })
              )}
            </div>
            <div className="px-5 py-2 border-t border-slate-200 text-[11px] text-slate-500 text-center">
              {backendIndexLoading
                ? "Loading extended catalog (DI Tank + Aspen Surgical)…"
                : `${pickerResults.length} of ${marketplaceIndex.length} SKUs shown. Refine search to see more.`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

